<script lang="ts" module>
  import type { PromptDict, PromptLines } from './modals.ts';

  interface Props<T extends string> {
    lines?: PromptLines;
    dict?: PromptDict;
    atomize?: boolean;
    onAction?: (action: T) => void;
    class?: string;
  }
</script>

<script lang="ts" generics="T extends string">
  import { asArray } from '@yp/common/containers.ts';
  import { isString, type FullOptional } from '@yp/common/identity.ts';
  import { onMount } from 'svelte';
  import { Button, Icon } from './index.ts';
  import {
    asLines,
    isPromptLine,
    segmentValue,
    type PromptSegment,
    type TypedSegment,
  } from './modals.ts';
  import { classes } from './util.ts';

  function toTyped(seg: PromptSegment): TypedSegment {
    return isString(seg) ? { type: 'text', value: seg } : seg;
  }

  const {
    lines,
    dict,
    atomize = true,
    onAction,
    class: propClass,
  }: FullOptional<Props<T>> = $props();

  const clazz = $derived(
    classes(
      propClass,
      isPromptLine(lines) ? undefined : lines?.clazz,
      'flex items-start items-center',
    ),
  );

  let rootEl: HTMLElement | undefined;
  onMount(() => {
    // Trying to solve a problem caused by changing the Android system font-size up two levels.
    // TODO: See if there's a more disciplined way of handling this.
    // https://stackoverflow.com/questions/21664940/force-browser-to-trigger-reflow-while-changing-css
    setTimeout(() => rootEl?.classList.toggle('p-line'));
  });
</script>

<div bind:this={rootEl} class="p-root">
  {#each asLines(lines) as line, i (i)}
    <div class={clazz}>
      <p class="flex items-start items-center flex-wrap gap-0.5">
        {#each asArray(line).map(toTyped) as seg, j (j)}
          {@const value = segmentValue(seg, dict)}
          <!-- DNS: Svelte 5 snippets help with conditional wrapping -->
          {#if seg.link}
            <a
              href={seg.link}
              class="text-blue-600 border-blue-600 border-b leading-none flex items-start items-center flex-wrap gap-0.5">
              {#if seg.type === 'icon'}
                <Icon icon={seg.value} class="inline-block {seg.clazz ?? ''}" />
              {:else if atomize}
                {#each value.split(/\s+/) as t, k (k)}
                  <span class={seg.clazz}>{t}</span>
                {/each}
              {:else}
                <span class={seg.clazz}>{value}</span>
              {/if}
            </a>
          {:else if seg.action}
            <Button
              name={seg.action}
              role="button"
              class={seg.clazz}
              onclick={() => onAction?.(seg.action!)}>
              {value}
            </Button>
          {:else if seg.type === 'icon'}
            <Icon icon={value} class="inline-block {seg.clazz ?? ''}" />
          {:else if atomize}
            {#each value.split(/\s+/) as t, l (l)}
              <span class={seg.clazz}>{t}</span>
            {/each}
          {:else}
            <span class={seg.clazz}>{value}</span>
          {/if}
        {/each}
        <span class="italic" />
      </p>
    </div>
  {/each}
</div>

<style lang="scss">
  .p-line span {
    overflow: hidden;
  }
</style>
